// Pagos asentados de UN crédito (`GET /credits/{creditId}/payments`), acumulando páginas.
import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { loanCreditApi, type LoanPaymentListItem } from '@/api/services/loan-credit';
import { userMessageOf } from '@/api/errors';
import { classifyFailure, type CreditFileFailure } from '../../creditFile/failures';

const PAYMENTS_KEY = ['credit-file', 'payments'] as const;

const PAGE_SIZE = 50;

const STALE_TIME_MS = 30_000;

export interface CreditPaymentsResult {
    /** En el orden del silo: del pago más reciente al más viejo. */
    payments: readonly LoanPaymentListItem[];
    isLoading: boolean;
    /** Falla de la PRIMERA página: sin nada cargado, el bloque dice por qué. */
    failure: CreditFileFailure | null;
    retry: () => void;
    hasNextPage: boolean;
    fetchNextPage: () => void;
    isFetchingNextPage: boolean;
    /** Por qué falló la página siguiente; las filas ya cargadas siguen. */
    nextPageFailure: string | null;
}

export function useCreditPayments(creditId: string): CreditPaymentsResult {
    const query = useInfiniteQuery({
        queryKey: [...PAYMENTS_KEY, creditId],
        queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
            loanCreditApi.listCreditPayments(creditId, {
                limit: PAGE_SIZE,
                ...(pageParam === undefined ? {} : { nextToken: pageParam }),
            }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (last) => last.nextToken,
        staleTime: STALE_TIME_MS,
        retry: false,
    });

    const payments = useMemo(
        () => query.data?.pages.flatMap((page) => page.items) ?? [],
        [query.data],
    );
    const hasPages = query.data !== undefined;

    return {
        payments,
        isLoading: query.isLoading,
        failure: hasPages ? null : classifyFailure(query.error),
        retry: () => {
            void query.refetch();
        },
        hasNextPage: query.hasNextPage,
        fetchNextPage: () => {
            void query.fetchNextPage();
        },
        isFetchingNextPage: query.isFetchingNextPage,
        nextPageFailure: query.isFetchNextPageError ? userMessageOf(query.error) : null,
    };
}
